import { useCallback, useMemo, useState } from 'react';
import type { Command } from '../../menu/types';
import type { GridFilter } from '../types';
import { combineAndFilter } from '../utils';

type VisibilityFilterToggle = {
  command: Command;
  filter: GridFilter;
  inactiveCaption?: string;
  activeCaption?: string;
};

const getVisibilityFilterToggles = (commands?: Command[]): VisibilityFilterToggle[] =>
  (commands ?? []).flatMap((command) => {
    if (!command.settings) return [];

    try {
      const settings = JSON.parse(command.settings) as {
        filter?: GridFilter;
        inactiveCaption?: string;
        activeCaption?: string;
      };
      if (!Array.isArray(settings.filter)) return [];

      return [
        {
          command,
          filter: settings.filter,
          inactiveCaption: settings.inactiveCaption,
          activeCaption: settings.activeCaption,
        },
      ];
    } catch {
      return [];
    }
  });

export const useConfiguredGridVisibilityFilters = (
  commands: Command[] | undefined,
  resetKey: string
) => {
  const [states, setStates] = useState<Record<string, boolean>>({});
  const toggles = useMemo(() => getVisibilityFilterToggles(commands), [commands]);
  const stateKey = useCallback((command: Command) => `${resetKey}:${command.key}`, [resetKey]);

  const apply = useCallback(
    (userFilter: GridFilter | undefined) => {
      const visibilityFilter = toggles.reduce<GridFilter | undefined>(
        (result, toggle) =>
          states[stateKey(toggle.command)] ? result : combineAndFilter(result, toggle.filter),
        undefined
      );
      return combineAndFilter(visibilityFilter, userFilter);
    },
    [stateKey, states, toggles]
  );

  const toggle = useCallback(
    (command: Command) => {
      const key = stateKey(command);
      setStates((current) => ({ ...current, [key]: !current[key] }));
    },
    [stateKey]
  );

  const commandCaptionOverrides = useMemo(
    () =>
      Object.fromEntries(
        toggles.flatMap((configuredToggle) => {
          const caption = states[stateKey(configuredToggle.command)]
            ? configuredToggle.activeCaption
            : configuredToggle.inactiveCaption;
          return caption ? [[configuredToggle.command.key, caption]] : [];
        })
      ),
    [stateKey, states, toggles]
  );

  return { apply, toggle, commandCaptionOverrides };
};
